/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Badge,
    Box,
    Button,
    Flex,
    Center,
    useMultiStyleConfig,
    useMediaQuery
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@consteed/overrides/components/link'
import {BasketIcon, BrandLogo, BrandLogoWithoutClaim} from '@consteed/overrides/components/icons'
import {HOME_HREF} from '@salesforce/retail-react-app/app/constants'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import useShrinkedHeader from '@consteed/overrides/hooks/use-shrinked-header'

const CheckoutHeader = () => {
    const intl = useIntl()
    const {
        derivedData: {totalItems}
    } = useCurrentBasket()

    const [isDesktop] = useMediaQuery('(min-width: 1200px)')
    const isShrunk = useShrinkedHeader()
    const styles = useMultiStyleConfig('Header', {isShrunk})

    return (
        <Box {...styles.container}>
            <Box {...styles.content}>
                <Flex h={{base: '52px', xl: '110px'}} align="center" justify="space-between">
                    <Box>
                        <Link
                            href={HOME_HREF}
                            title="Back to homepage"
                            {...styles.logoContainer}
                            {...styles.icons}
                            h={{base: '52px'}}
                            display={{base: 'inlineFlex'}}
                            alignItems={{base: 'center'}}
                        >
                            {!isDesktop || isShrunk ? (
                                <BrandLogoWithoutClaim {...styles.logo} />
                            ) : (
                                <BrandLogo {...styles.logo} />
                            )}
                        </Link>
                    </Box>

                    <Button
                        as={Link}
                        href="/cart"
                        display="inline-flex"
                        variant="unstyled"
                        color="white"
                        {...styles.icons}
                        {...styles.cartIcon}
                        rightIcon={
                            <Center position="relative" width={11} height={11}>
                                <BasketIcon position="absolute" left="0px" />
                                <Badge variant="notification">{totalItems}</Badge>
                            </Center>
                        }
                        aria-label={intl.formatMessage(
                            {
                                id: 'checkout_header.link.assistive_msg.cart',
                                defaultMessage: 'Back to cart, number of items: {numItems}'
                            },
                            {numItems: totalItems}
                        )}
                    >
                        <FormattedMessage
                            defaultMessage="Back to cart"
                            id="checkout_header.link.cart"
                        />
                    </Button>
                </Flex>
            </Box>
        </Box>
    )
}

export default CheckoutHeader
